import React, { useState } from "react";
import * as Yup from "yup";
import { useHistory, Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";

import {
	Button,
	CssBaseline,
	TextField,
	Grid,
	Typography,
	Container,
	InputAdornment,
	IconButton,
	Link,
	Paper,
	CircularProgress
} from '@material-ui/core';

import { Visibility, VisibilityOff, PersonOutline, EmailOutlined, LockOutlined } from '@material-ui/icons';
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		minHeight: '100vh',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		background: '#31357b',
		padding: theme.spacing(2),
	},
	paper: {
		padding: theme.spacing(3),
		[theme.breakpoints.up('sm')]: {
			padding: theme.spacing(5),
		},
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		borderRadius: "10px",
		boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
		backgroundColor: "rgba(255, 255, 255, 0.98)",
		width: '100%',
	},
	logo: {
		width: "120px",
		[theme.breakpoints.up('sm')]: {
			width: "140px",
		},
		marginBottom: theme.spacing(2),
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(2),
	},
	textField: {
		"& .MuiOutlinedInput-root": {
			borderRadius: "12px",
		},
	},
	submit: {
		margin: theme.spacing(2, 0, 2),
		padding: theme.spacing(1),
		borderRadius: "8px",
		fontWeight: "bold",
		textTransform: "none",
		fontSize: "1rem",
		background: "#0035a2",
		color: '#fff'
	},
	footerText: {
		color: 'white',
		marginTop: theme.spacing(3),
		textAlign: 'center',
		fontSize: '0.85rem',
		opacity: 0.8,
	}
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto!")
		.max(50, "Muito longo!")
		.required("Obrigatório"),
	password: Yup.string()
		.min(5, "Muito curta!")
		.max(50, "Muito longa!")
		.required("Obrigatório"),
	email: Yup.string()
		.email("E-mail inválido")
		.required("Obrigatório"),
});

const SignUp = () => {
	const classes = useStyles();
	const history = useHistory();

	const [showPassword, setShowPassword] = useState(false);
	const initialState = { name: "", email: "", password: "" };

	const handleSignUp = async values => {
		try {
			await api.post("/auth/signup", values);
			toast.success(i18n.t("signup.toasts.success"));
			history.push("/login");
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<Container component="main" maxWidth="xs" disableGutters>
				<Paper elevation={0} className={classes.paper}>
					<img
						src="https://www.nrwork.com.br/conteudo/imagem/e44315dff97ddb544b2c523e3fc64b71_g.png"
						alt="Logo"
						className={classes.logo}
					/>

					<Typography variant="body2" color="textSecondary" align="center">
						Crie sua conta gratuitamente
					</Typography>

					<Formik
						initialValues={initialState}
						enableReinitialize={true}
						validationSchema={UserSchema}
						onSubmit={async (values, actions) => {
							await handleSignUp(values);
							actions.setSubmitting(false);
						}}
					>
						{({ touched, errors, isSubmitting }) => (
							<Form className={classes.form}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Field
											as={TextField}
											autoComplete="name"
											name="name"
											size="medium"
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											fullWidth
											id="name"
											label={i18n.t("signup.form.name")}
											className={classes.textField}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<PersonOutline color="action" fontSize="small" />
													</InputAdornment>
												),
											}}
										/>
									</Grid>

									<Grid item xs={12}>
										<Field
											as={TextField}
											variant="outlined"
											fullWidth
											id="email"
											size="medium"
											label={i18n.t("signup.form.email")}
											name="email"
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											autoComplete="email"
											className={classes.textField}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<EmailOutlined color="action" fontSize="small" />
													</InputAdornment>
												),
											}}
										/>
									</Grid>

									<Grid item xs={12}>
										<Field
											as={TextField}
											variant="outlined"
											fullWidth
											name="password"
											size="medium"
											id="password"
											autoComplete="new-password"
											error={touched.password && Boolean(errors.password)}
											helperText={touched.password && errors.password}
											label={i18n.t("signup.form.password")}
											type={showPassword ? 'text' : 'password'}
											className={classes.textField}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<LockOutlined color="action" fontSize="small" />
													</InputAdornment>
												),
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															onClick={() => setShowPassword(!showPassword)}
															edge="end"
															size="small"
														>
															{showPassword ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												)
											}}
										/>
									</Grid>
								</Grid>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									color="primary"
									className={classes.submit}
									disabled={isSubmitting}
									disableElevation
								>
									{isSubmitting ? <CircularProgress size={24} color="inherit" /> : i18n.t("signup.buttons.submit")}
								</Button>

								<Grid container justifyContent="center">
									<Grid item>
										<Typography variant="body2"> 
											<Link
												component={RouterLink}
												to="/login"
												style={{ fontWeight: 600, textDecoration: 'none' }}
											>
												{i18n.t("signup.buttons.login")}
											</Link>
										</Typography>
									</Grid>
								</Grid>
							</Form>
						)}
					</Formik>
				</Paper>

				<Typography className={classes.footerText}>
					© {new Date().getFullYear()} NR Work - Todos os direitos reservados.
				</Typography>
			</Container>
		</div>
	);
};

export default SignUp;